import { Spacing } from '@persona-id/design-tokens';
import produce from 'immer';
import { difference, uniqBy } from 'lodash/fp';
import { ChangeEvent, memo, ReactNode, useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import { ModalBody, ModalContainer, ModalFooter, ModalHeader } from 'dashboard/layouts/ModalLayout';
import TableLayout, {
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import {
  CountryCodeLower,
  GovernmentIdEnabledIdClassByCountries,
  GovernmentIdEnabledIdClassByCountryEntry,
  GovernmentIdSide,
} from 'dashboard/models/verification-template-version';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import { Label, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { GOVERNMENT_ID_DISPLAY_STRINGS, GovernmentIdClass } from 'lib/constants/governmentIds';

const CONFIGURE_IDS_SIDES_OPTIONS: {
  label: string;
  value: GovernmentIdSide;
}[] = [
  { label: 'Front', value: GovernmentIdSide.Front },
  { label: 'Back', value: GovernmentIdSide.Back },
  { label: 'Signature', value: GovernmentIdSide.PassportSignature },
  { label: 'Barcode', value: GovernmentIdSide.BarcodePdf417 },
];

export const ALL_CONFIGURE_IDS_SIDES = CONFIGURE_IDS_SIDES_OPTIONS.map(
  (sideOption) => sideOption.value,
);

const StyledModalContainer = styled(ModalContainer)`
  width: clamp(775px, 90vw, 1000px);
  height: clamp(722px, 75vh, 1000px);
`;

const StyledModalParagraph = styled(Paragraph)`
  padding-bottom: ${Spacing[5]};
`;

const StyledCheckbox = styled(Checkbox)`
  display: inline-block;
  vertical-align: middle;
  margin-right: ${Spacing[4]};
`;

const ConfigureIdTableModalBody = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const ConfigureIdTableContainer = styled('div')`
  display: inline-flex;
  overflow: scroll;
  width: 100%;
  flex: 0 1 auto;

  & > div {
    width: 100%;
  }
`;

const ConfigureIdsTableColumnLabel = styled(Label).attrs(() => ({
  size: 'sm',
}))`
  display: inline-block;
  font-size: ${({ theme }) => theme.text.paragraph.sm.fontSize};
  font-weight: ${({ theme }) => theme.text.paragraph.sm.fontWeight};
  color: ${({ theme }) => theme.color.text.secondary};
  white-space: pre-line;
`;

const ConfigureIdsTableCheckboxContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const idTypeCellCSS = css`
  border-right: 1px solid ${({ theme }) => theme.color.border.primary};
  min-width: 180px;
`;

const ConfigureIdTableTypeTH = styled(TableTH)`
  ${idTypeCellCSS}
`;

const ConfigureIdTableTR = styled(TableTR)`
  [role='cell']:first-child {
    ${idTypeCellCSS}
  }
`;

const IdSupportTooltipContent = styled.div`
  max-width: 300px;
  max-height: 200px;
  overflow: scroll;
  color: ${({ theme }) => theme.color.text.secondary};
`;

type IdSupport = {
  countries: CountryCodeLower[];
  sides: GovernmentIdSide[];
  partiallySupportedSides?: GovernmentIdSide[];
};

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export type SideSupportByIdClass = Record<GovernmentIdClass, IdSupport>;

type SideSupportByIdClassTuple = [GovernmentIdClass, IdSupport];

type GovernmentIdEnabledIdClassByCountriesTuple = [
  CountryCodeLower,
  GovernmentIdEnabledIdClassByCountryEntry[],
];

// method to derive which sides are configurable for each id type
export const isSideSupportedByID = ({
  id,
  side,
  supportMap,
  country,
}: {
  id: GovernmentIdClass;
  side: GovernmentIdSide;
  supportMap: IdSupport;
  country?: CountryCodeLower;
}) => {
  if (id === 'pp') {
    return [GovernmentIdSide.Front, GovernmentIdSide.PassportSignature].includes(side);
  }

  const containsSpecialCountry =
    country != null
      ? ['ca', 'us'].includes(country)
      : supportMap.countries.includes('ca') || supportMap.countries.includes('us');

  if ((id === 'dl' || id === 'id') && containsSpecialCountry) {
    return [GovernmentIdSide.Front, GovernmentIdSide.Back, GovernmentIdSide.BarcodePdf417].includes(
      side,
    );
  }

  return [GovernmentIdSide.Front, GovernmentIdSide.Back].includes(side);
};

type ConfigureIdsTableProps = {
  checkedCountries: CountryCodeLower[];
  defaultCountryIdClasses: Record<CountryCodeLower, GovernmentIdEnabledIdClassByCountryEntry[]>;
  enabledIdClassesByCountry: GovernmentIdEnabledIdClassByCountries;
  hideConfigureIdsModal: () => void;
  onApply: (updatedConfigSupport: GovernmentIdEnabledIdClassByCountries) => void;
};

const toggleSideSupportForId = ({
  idType,
  supportMap,
  checked,
  governmentIdSide,
}: {
  idType: GovernmentIdClass;
  supportMap: SideSupportByIdClass;
  checked: boolean;
  governmentIdSide: GovernmentIdSide;
}): GovernmentIdSide[] => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const sideSupportArray = [...supportMap[idType].sides];

  if (checked) {
    if (!sideSupportArray.includes(governmentIdSide)) {
      sideSupportArray.push(governmentIdSide);
    }
    return sideSupportArray;
  } else {
    return sideSupportArray.filter((side: GovernmentIdSide) => side !== governmentIdSide);
  }
};

export const deriveDefaultIdConfiguration = ({
  checkedCountries,
  defaultCountryIdClasses,
  enabledIdClassesByCountry,
}: {
  checkedCountries: CountryCodeLower[];
  defaultCountryIdClasses: Record<CountryCodeLower, GovernmentIdEnabledIdClassByCountryEntry[]>;
  enabledIdClassesByCountry: GovernmentIdEnabledIdClassByCountries;
}): SideSupportByIdClass => {
  const supportMap: SideSupportByIdClass = {} as SideSupportByIdClass;

  // loop through each selected country's id support
  // and create a mapping of id to an object containing
  // - sides: an array of all currently enabled sides
  // - countries: an array of all countries that support the id class
  checkedCountries.forEach((country: CountryCodeLower) => {
    defaultCountryIdClasses[country].forEach((idClass) => {
      const idClassType = idClass.class;
      const enabledIdSettings = enabledIdClassesByCountry[country]?.find(
        (enabledIdClass) => enabledIdClass.class === idClassType,
      );

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (!(idClassType in supportMap)) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        supportMap[idClassType] = {
          countries: [country],
          sides: enabledIdSettings ? Array.from(enabledIdSettings['requires-sides']) : [],
        };
      } else {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        supportMap[idClassType].countries.push(country);
      }

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (Boolean(idClassType in supportMap) && enabledIdSettings) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const diff = difference(enabledIdSettings['requires-sides'], supportMap[idClassType].sides);

        if (diff.length) {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          supportMap[idClassType].sides = supportMap[idClassType].sides.concat(diff);
        }
      }
    });
  });

  // - partiallySupportedSides: array of sides supported by one country, but not another
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  Object.entries(supportMap).forEach(([supportId, configSupportMap]: SideSupportByIdClassTuple) => {
    let partialSupportedSides: GovernmentIdSide[] = [];
    configSupportMap.countries.forEach((country) => {
      const supportEnabledByCountry = enabledIdClassesByCountry[country]?.find(
        (idClass) => idClass.class === supportId,
      );

      if (supportEnabledByCountry == null) {
        partialSupportedSides.concat(
          ALL_CONFIGURE_IDS_SIDES.filter((side) =>
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            isSideSupportedByID({ id: supportId, side, supportMap: supportMap[supportId] }),
          ),
        );
      } else {
        // first find all the sides that aren't checked by an enabled country
        // then only add those sides if they don't already exist in the array (so no dupes)
        const unsupportedSides = configSupportMap.sides.filter(
          (side) => !supportEnabledByCountry['requires-sides'].includes(side),
        );

        partialSupportedSides = partialSupportedSides.concat(
          unsupportedSides.filter((side) => !partialSupportedSides.includes(side)),
        );
      }
    });

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    supportMap[supportId].partiallySupportedSides = partialSupportedSides;
  });

  return supportMap;
};

const removeSideFromPartialSupport = ({
  idSupport,
  idType,
  governmentIdSide,
}: {
  idSupport: SideSupportByIdClass;
  idType: GovernmentIdClass;
  governmentIdSide: GovernmentIdSide;
}) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const sideIndex = idSupport[idType].partiallySupportedSides.findIndex(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (partialSide) => partialSide === governmentIdSide,
  );

  if (sideIndex > -1) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    idSupport[idType].partiallySupportedSides.splice(sideIndex, 1);
  }
};

/**
 * Returns the list of countries x ID classes post bulk change.
 *
 * We're transforming idSupport into GovernmentIdEnabledIdClassByCountries by referencing the currently enabled country x ID class configs,
 * and the default country x ID class configs.
 *
 * @param checkedCountries - Countries that this bulk change will apply to.
 * @param idSupport - The list of sides (e.g., Front, Back, Signature page) of a government ID x ID class that *should be supported*.
 * @param defaultCountryIdClasses - Global, Persona-wide default configs per country x ID class.
 * @param enabledIdClassesByCountry - Pre bulk change enabled country x ID class configs.
 *
 * @returns {GovernmentIdEnabledIdClassByCountries}
 */
export const updateIdConfigurationSupport = ({
  checkedCountries,
  idSupport,
  defaultCountryIdClasses,
  enabledIdClassesByCountry,
}: {
  checkedCountries: CountryCodeLower[];
  idSupport: SideSupportByIdClass;
  defaultCountryIdClasses: Record<CountryCodeLower, GovernmentIdEnabledIdClassByCountryEntry[]>;
  enabledIdClassesByCountry: GovernmentIdEnabledIdClassByCountries;
}): GovernmentIdEnabledIdClassByCountries => {
  const finalSupport: GovernmentIdEnabledIdClassByCountries =
    {} as GovernmentIdEnabledIdClassByCountries;

  // first create all the new configurations for checked countries
  checkedCountries.forEach((country: CountryCodeLower) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    Object.entries(idSupport).forEach(([supportId, supportMap]: SideSupportByIdClassTuple) => {
      if (supportMap.countries.includes(country) && supportMap.sides.length > 0) {
        const defaultSupport = Object.assign(
          {},
          defaultCountryIdClasses[country].find((supportClass) => supportClass.class === supportId),
        );

        const existingSupport = enabledIdClassesByCountry[country]?.find(
          (idClassSupport) => idClassSupport.class === supportId,
        );

        const requiredSupportedSides = supportMap.sides.filter((side) =>
          isSideSupportedByID({ side, id: supportId, supportMap, country }),
        );

        const acceptedClasses = existingSupport?.['accepted-classes'] ??
          defaultSupport?.['accepted-classes'] ?? [supportId];
        const support = {
          class: supportId,
          'requires-sides': Array.from(new Set(requiredSupportedSides)),
          'requires-device': defaultSupport['requires-device'],
          'accepted-classes': acceptedClasses,
        };

        if (!finalSupport[country]) {
          finalSupport[country] = [support];
        } else {
          finalSupport[country].push(support);
        }
      }
    });
  });

  // then add any configurations for countries that are enabled, but not checked
  Object.entries(enabledIdClassesByCountry)
    .filter(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ([country, _supportMap]: GovernmentIdEnabledIdClassByCountriesTuple) =>
        !checkedCountries.includes(country),
    )
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    .forEach(([country, supportMap]: GovernmentIdEnabledIdClassByCountriesTuple) => {
      finalSupport[country] = supportMap;
    });

  // Ensure unique entries
  Object.entries(finalSupport).forEach(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    ([country, supportMap]: GovernmentIdEnabledIdClassByCountriesTuple) => {
      finalSupport[country] = uniqBy('class', supportMap);
    },
  );

  return finalSupport;
};

const ConfigureIdsTable = memo(
  ({
    checkedCountries,
    defaultCountryIdClasses,
    enabledIdClassesByCountry,
    hideConfigureIdsModal,
    onApply,
  }: ConfigureIdsTableProps) => {
    const defaultIdConfiguration: SideSupportByIdClass = useMemo(
      () =>
        deriveDefaultIdConfiguration({
          checkedCountries,
          defaultCountryIdClasses,
          enabledIdClassesByCountry,
        }),

      [checkedCountries, defaultCountryIdClasses, enabledIdClassesByCountry],
    );

    const [idSupport, setIdSupport] = useState<SideSupportByIdClass>(defaultIdConfiguration);

    const toggleAllIds = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        setIdSupport((prevIdSupport) => {
          const newSupport = produce(prevIdSupport, (newState) => {
            for (const type in idSupport) {
              const idType = type as GovernmentIdClass;
              if (e.target.checked) {
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                const sideSupportArray = newState[idType].sides;
                ALL_CONFIGURE_IDS_SIDES.forEach((side: GovernmentIdSide) => {
                  if (
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    isSideSupportedByID({ id: idType, side, supportMap: prevIdSupport[idType] }) &&
                    !sideSupportArray.includes(side)
                  ) {
                    sideSupportArray.push(side);
                  }
                });
              } else {
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                newState[idType as GovernmentIdClass].sides = [];
              }

              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              newState[idType].partiallySupportedSides = [];
            }
          });

          return newSupport;
        });
      },
      [idSupport],
    );

    const toggleColumnSideSupport = (
      e: ChangeEvent<HTMLInputElement>,
      governmentIdSide: GovernmentIdSide,
    ) => {
      setIdSupport((prevIdSupport) => {
        const supportMap = produce(prevIdSupport, (newState) => {
          for (const type in newState) {
            const idType = type as GovernmentIdClass;
            if (
              isSideSupportedByID({
                id: idType,
                side: governmentIdSide,
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                supportMap: prevIdSupport[idType],
              })
            ) {
              removeSideFromPartialSupport({ idSupport: newState, governmentIdSide, idType });
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              newState[idType].sides = toggleSideSupportForId({
                idType: idType,
                supportMap: newState,
                checked: e.target.checked,
                governmentIdSide,
              });
            }
          }
        });

        return supportMap;
      });
    };

    const toggleIdRowSupport = (e: ChangeEvent<HTMLInputElement>, idType: GovernmentIdClass) => {
      setIdSupport((prevIdSupport) => {
        const newSupport = produce(prevIdSupport, (newState) => {
          if (e.target.checked) {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            newState[idType].sides = ALL_CONFIGURE_IDS_SIDES.filter((side) =>
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              isSideSupportedByID({ id: idType, side, supportMap: prevIdSupport[idType] }),
            );
          } else {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            newState[idType].sides = [];
          }
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          newState[idType].partiallySupportedSides = [];
        });

        return newSupport;
      });
    };

    const toggleIdSideSupport = (
      e: ChangeEvent<HTMLInputElement>,
      idType: GovernmentIdClass,
      governmentIdSide: GovernmentIdSide,
    ) => {
      setIdSupport((prevIdSupport) => {
        const supportMap = produce(prevIdSupport, (newState) => {
          removeSideFromPartialSupport({
            idSupport: newState,
            governmentIdSide,
            idType,
          });
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          newState[idType].sides = toggleSideSupportForId({
            idType,
            supportMap: newState,
            checked: e.target.checked,
            governmentIdSide,
          });
        });

        return supportMap;
      });
    };

    const applyConfigurations = useCallback(() => {
      const updatedConfigurationSupport = updateIdConfigurationSupport({
        checkedCountries,
        idSupport,
        defaultCountryIdClasses,
        enabledIdClassesByCountry,
      });
      onApply(updatedConfigurationSupport);
      hideConfigureIdsModal();
    }, [
      checkedCountries,
      enabledIdClassesByCountry,
      defaultCountryIdClasses,
      hideConfigureIdsModal,
      idSupport,
      onApply,
    ]);

    const supportRows = useMemo(() => {
      const rows: ReactNode[] = [];
      const sortedIdSupport = Object.fromEntries(
        Object.entries(idSupport).sort(
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          (a: SideSupportByIdClassTuple, b: SideSupportByIdClassTuple) => {
            const typeA = a[0];
            const typeB = b[0];
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            if (typeA > typeB) {
              return 1;
            }

            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            if (typeA < typeB) {
              return -1;
            }
            return 0;
          },
        ),
      ) as SideSupportByIdClass;

      Object.entries(sortedIdSupport).forEach(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ([supportId, supportMap]: SideSupportByIdClassTuple) => {
          const hasCountrySubsetSupport =
            supportMap.countries.length > 1 &&
            checkedCountries.length > 1 &&
            supportMap.countries.length !== checkedCountries.length;

          const idClassNameLabel = (
            <ConfigureIdsTableColumnLabel htmlFor={`configure-ids-table__${supportId}`}>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              {GOVERNMENT_ID_DISPLAY_STRINGS[supportId]}
              {hasCountrySubsetSupport && (
                <span>
                  {'\n'}(Available in {supportMap.countries.length} countries)
                </span>
              )}
              {supportMap.countries.length === 1 && checkedCountries.length > 1 && (
                <span>
                  {'\n'}({supportMap.countries[0].toUpperCase()} only)
                </span>
              )}
            </ConfigureIdsTableColumnLabel>
          );

          const idSupportedSides = ALL_CONFIGURE_IDS_SIDES.filter((side) =>
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            isSideSupportedByID({ id: supportId, side, supportMap: idSupport[supportId] }),
          );

          rows.push(
            <ConfigureIdTableTR key={`${supportId}-row`}>
              <TableTD>
                <ConfigureIdsTableCheckboxContainer>
                  <StyledCheckbox
                    id={`configure-ids-table__${supportId}`}
                    checked={supportMap.sides.length === idSupportedSides.length}
                    indeterminate={
                      supportMap.sides.length > 0 &&
                      supportMap.sides.length !== idSupportedSides.length
                    }
                    onChange={(e) => {
                      toggleIdRowSupport(e, supportId);
                    }}
                  />
                  {hasCountrySubsetSupport ? (
                    <Tooltip portal={false} trigger={idClassNameLabel}>
                      <IdSupportTooltipContent>
                        {supportMap.countries.map((country) => country.toUpperCase()).join(', ')}
                      </IdSupportTooltipContent>
                    </Tooltip>
                  ) : (
                    idClassNameLabel
                  )}
                </ConfigureIdsTableCheckboxContainer>
              </TableTD>
              {ALL_CONFIGURE_IDS_SIDES.map((side: GovernmentIdSide) => (
                <TableTD key={`${supportId}-${side}`}>
                  {isSideSupportedByID({
                    id: supportId,
                    side,
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    supportMap: idSupport[supportId],
                  }) && (
                    <StyledCheckbox
                      id={`configure-ids-table__${supportId}-${side}`}
                      data-test={`configure-ids-table__${supportId}-${side}`}
                      checked={supportMap.sides.includes(side)}
                      indeterminate={
                        checkedCountries.length > 1 &&
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        supportMap.partiallySupportedSides.includes(side)
                      }
                      onChange={(e) => {
                        toggleIdSideSupport(e, supportId, side);
                      }}
                    />
                  )}
                </TableTD>
              ))}
            </ConfigureIdTableTR>,
          );
        },
      );

      return rows;
    }, [idSupport, checkedCountries]);

    const subsetOfSidesUnchecked = Object.entries(idSupport).some(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ([supportId, supportMap]: SideSupportByIdClassTuple) =>
        supportMap.sides.length <
        ALL_CONFIGURE_IDS_SIDES.filter((side) =>
          isSideSupportedByID({ id: supportId, side, supportMap }),
        ).length,
    );

    const allSidesUnchecked = Object.entries(idSupport).every(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ([_, supportMap]: SideSupportByIdClassTuple) => supportMap.sides.length === 0,
    );

    const allIdsAndSidesChecked = Object.entries(idSupport).every(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ([supportId, supportMap]: SideSupportByIdClassTuple) =>
        supportMap.sides.length ===
        ALL_CONFIGURE_IDS_SIDES.filter((side) =>
          isSideSupportedByID({ id: supportId, side, supportMap }),
        ).length,
    );

    return (
      <StyledModalContainer>
        <ModalHeader onClose={hideConfigureIdsModal}>ID configurations</ModalHeader>
        <ModalBody>
          <ConfigureIdTableModalBody>
            <StyledModalParagraph>
              Configure accepted ID types and required ID attributes. Note that the list below
              includes all of the ID types from the selected countries, and not every ID type exists
              for every country.
            </StyledModalParagraph>
            <ConfigureIdTableContainer>
              <TableLayout
                showHeader
                nullState={
                  <TableNullState dataTestBlock='country-configurations-table__configure-ids-table'>
                    Select countries to configure ids
                  </TableNullState>
                }
                headerTR={
                  <>
                    <ConfigureIdTableTypeTH isSticky>
                      <ConfigureIdsTableCheckboxContainer>
                        <StyledCheckbox
                          id='configure-ids-table__all-ids'
                          onChange={toggleAllIds}
                          indeterminate={!allSidesUnchecked && subsetOfSidesUnchecked}
                          checked={allIdsAndSidesChecked}
                        />
                        <ConfigureIdsTableColumnLabel htmlFor='configure-ids-table__all-ids'>
                          ID types
                          {`(${Object.keys(idSupport).length})`}
                        </ConfigureIdsTableColumnLabel>
                      </ConfigureIdsTableCheckboxContainer>
                    </ConfigureIdTableTypeTH>
                    {CONFIGURE_IDS_SIDES_OPTIONS.map((side) => {
                      const idsWithSideSupport = Object.entries(idSupport).filter(
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        ([supportId, supportMap]: SideSupportByIdClassTuple) =>
                          isSideSupportedByID({
                            id: supportId,
                            side: side.value,
                            supportMap,
                          }),
                      );

                      const subsetAreChecked = idsWithSideSupport.some(([_, supportMap]) =>
                        supportMap.sides.includes(side.value),
                      );

                      const subsetAreUnchecked = idsWithSideSupport.some(
                        ([_, supportMap]) => !supportMap.sides.includes(side.value),
                      );
                      return (
                        <TableTH isSticky key={`id-side-${side.value}`}>
                          <ConfigureIdsTableCheckboxContainer>
                            <StyledCheckbox
                              id={`configure-ids-table__side-${side.value}`}
                              indeterminate={subsetAreChecked && subsetAreUnchecked}
                              checked={
                                idsWithSideSupport.length > 0 &&
                                idsWithSideSupport.every(([_, supportMap]) =>
                                  supportMap.sides.includes(side.value),
                                )
                              }
                              disabled={idsWithSideSupport.length === 0}
                              onChange={(e) => {
                                toggleColumnSideSupport(e, side.value);
                              }}
                            />
                            <ConfigureIdsTableColumnLabel
                              htmlFor={`configure-ids-table__side-${side.value}`}
                            >
                              {side.label}
                            </ConfigureIdsTableColumnLabel>
                          </ConfigureIdsTableCheckboxContainer>
                        </TableTH>
                      );
                    })}
                  </>
                }
              >
                {supportRows}
              </TableLayout>
            </ConfigureIdTableContainer>
          </ConfigureIdTableModalBody>
        </ModalBody>
        <ModalFooter>
          <Button
            id='configuration-ids-modal__close-button'
            onClick={hideConfigureIdsModal}
            variant='basic'
          >
            Cancel
          </Button>
          <Button
            id='configuration-ids-modal__confirm-button'
            onClick={applyConfigurations}
            variant='primary'
          >
            Apply
          </Button>
        </ModalFooter>
      </StyledModalContainer>
    );
  },
);

ConfigureIdsTable.displayName = 'ConfigureIdsTable';

export default ConfigureIdsTable;
